/**
 * The access token a request goes out with, read without queueing behind a
 * token refresh.
 *
 * supabase-js asks auth-js for the token before every PostgREST, Storage and
 * Functions request, and auth-js answers from inside a lock it also holds for
 * the whole of a refresh. So a refresh POST that hangs holds up every query in
 * the app, even though those queries already have a perfectly good token: the
 * refresh is ahead of them in the queue, not because they need anything from
 * it, but because both go through the same door. CER-147 bounded each attempt;
 * auth-js then retries until its own thirty-second ceiling and the ticker
 * starts another round, so a screen still waited about a minute (CER-166).
 *
 * The session auth-js keeps in storage is the single source of truth for the
 * current token, and reading it is a synchronous storage read. When that
 * session still has real life left in it, that token is the answer and no lock
 * is involved. Only when it is at or past the point where auth-js itself
 * considers it due for renewal do we ask auth-js, which refreshes, single-
 * flights concurrent callers, and reports failure - all of which stay exactly
 * as they were.
 */

/**
 * How much life a token must have left to be used as-is. This is auth-js's own
 * `EXPIRY_MARGIN_MS` (three thirty-second auto-refresh ticks) and deliberately
 * not a number of our own: inside the margin auth-js refreshes, so a token we
 * accept here is one auth-js would also have handed back unchanged.
 */
export const SESSION_EXPIRY_MARGIN_MS = 90_000;

/** A session a request can go out with: who it is, and the token that says so. */
export interface ActiveSession {
  access_token: string;
  user: { id: string };
}

/**
 * A session read back from storage. auth-js's own `_isValidSession` requires
 * the same fields, so anything short of them is not a session it would have
 * used either.
 */
export interface StoredSession extends ActiveSession {
  /** Seconds since the epoch, as auth-js writes it. */
  expires_at: number;
}

/**
 * The session auth-js has in storage, or null when there is none, when what is
 * there is not a session, or when it is missing an expiry or a user - in which
 * case we cannot judge it and must ask auth-js rather than guess.
 */
export function parseStoredSession(raw: string | null | undefined): StoredSession | null {
  if (!raw) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }
  const candidate = parsed as Partial<StoredSession>;
  if (typeof candidate.access_token !== 'string' || !candidate.access_token) {
    return null;
  }
  if (typeof candidate.expires_at !== 'number') {
    return null;
  }
  if (typeof candidate.user?.id !== 'string' || !candidate.user.id) {
    return null;
  }
  return candidate as StoredSession;
}

/** True when this session may be used as it stands, with no refresh first. */
export function sessionIsUsable(session: StoredSession | null, nowMs: number = Date.now()): boolean {
  if (!session) {
    return false;
  }
  return session.expires_at * 1000 - nowMs >= SESSION_EXPIRY_MARGIN_MS;
}

export interface SessionSourceOptions<S extends ActiveSession> {
  /** The raw string auth-js stores under its storage key, read synchronously. */
  readStoredSession: () => string | null;
  /**
   * auth-js's own `getSession`. Called only when the stored session is missing,
   * unreadable or due for renewal; it refreshes, single-flights concurrent
   * callers, and is the one place a refresh failure is reported.
   */
  getSession: () => Promise<{
    data: { session: S | null };
    error: { message: string } | null;
  }>;
}

export interface SessionSource<S extends ActiveSession> {
  /**
   * The current session, refreshed first if it is due. Throws when the refresh
   * fails, so a screen shows what happened instead of retrying a dead token.
   */
  currentSession: () => Promise<S | null>;
  /**
   * The token to put on a request, for supabase-js's `accessToken` option.
   * Falls back to the publishable key when signed out, which is what
   * supabase-js does with an absent session.
   */
  accessToken: (publishableKey: string) => Promise<string>;
}

/** Auth rejected a refresh and removed its session; this is a confirmed sign-out. */
export class SessionEndedError extends Error {
  constructor(reason: string) {
    super(`You were signed out; sign in and try again. ${reason}`);
    this.name = 'SessionEndedError';
  }
}

export function createSessionSource<S extends ActiveSession>({
  readStoredSession,
  getSession,
}: SessionSourceOptions<S>): SessionSource<S> {
  const currentSession = async (): Promise<S | null> => {
    const stored = parseStoredSession(readStoredSession());
    if (sessionIsUsable(stored)) {
      // What is in storage is what auth-js wrote there, read back whole; the
      // fields checked above are the ones this decision needs, not all of them.
      return stored as unknown as S;
    }
    // Due for renewal, or nothing we can read. auth-js decides from here.
    const { data, error } = await getSession();
    if (error) {
      if (stored && !parseStoredSession(readStoredSession())) throw new SessionEndedError(error.message);
      throw new Error(error.message);
    }
    return data.session;
  };

  return {
    currentSession,
    accessToken: async (publishableKey: string) => {
      const session = await currentSession();
      return session?.access_token ?? publishableKey;
    },
  };
}
